import { cx } from "@emotion/css";
import { Chip } from "@mui/material";
import { Trash2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { OutlinedTextBlackButton } from "src/components/Default/LekUI/Button";
import { ScrollShadowWrapper } from "src/components/Default/LekUI/ScrollShadowWrapper";
import { Tooltip } from "src/components/Default/LekUI/Tooltip";
import type { ActiveFilter } from "src/helpers/getActiveFilters";

type ActiveFiltersChipsProps = {
	filters: ActiveFilter[];
	onEdit: (filter: ActiveFilter) => void;
	onRemove: (filter: ActiveFilter) => void;
	onClearAll: () => void;
	/** Zawsze jedna linia z bocznym scrollem (domyślnie zawija, poza mobilką). */
	nowrap?: boolean;
};

export function ActiveFiltersChips({
	filters,
	onEdit,
	onRemove,
	onClearAll,
	nowrap,
}: ActiveFiltersChipsProps) {
	const { t } = useTranslation();

	if (!filters.length) return null;

	const isGrouped = ({ values }: ActiveFilter) =>
		values.length > 2 || values.some((value) => !value);

	const summary = (filter: ActiveFilter) =>
		isGrouped(filter)
			? t("search.filters.selected", { count: filter.values.length })
			: filter.values.join(", ");

	return (
		<ScrollShadowWrapper>
			<div
				className={cx("search-filter-chips", {
					"search-filter-chips--nowrap": nowrap,
				})}
			>
				{filters.map((filter) => (
					<Tooltip
						key={filter.key}
						wrapperClassName="search-filter-chip-wrapper"
						// pełna treść w tooltipie — zgrupowane „N wybrane” i długie wartości ucięte wielokropkiem
						disabled={!filter.values.some(Boolean)}
						title={filter.values.filter(Boolean).join(", ")}
					>
						<Chip
							className="search-filter-chip"
							label={
								<>
									{filter.label}:{" "}
									<span className="text-bold">{summary(filter)}</span>
								</>
							}
							onClick={() => onEdit(filter)}
							onDelete={() => onRemove(filter)}
							deleteIcon={
								<X
									aria-label={t("search.filters.remove", {
										name: filter.label,
									})}
								/>
							}
							data-test-id={`search-filter-chip-${filter.key}`}
						/>
					</Tooltip>
				))}
				<span className="search-filter-chips__divider" />
				<OutlinedTextBlackButton
					className="search-filter-chips__clear"
					onClick={onClearAll}
					startIcon={<Trash2 className="icon-super-small" />}
					data-test-id="search-filters-clear-all"
				>
					{t("search.filters.clear", "Wyczyść")}
				</OutlinedTextBlackButton>
			</div>
		</ScrollShadowWrapper>
	);
}
